import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import SegmentPlugin from "./segmentPlugin";
import HeapPlugin from "./heapPlugin";
import { isProd } from "../../../env";

export interface AnalyticsPlugin {
  trackEvent: (name: string, params?: object) => void;
  trackUser: (userId?: string, params?: object) => void;
  addScript: VoidFunction;
}

interface AnalyticsContextValue {
  trackEvent: (name: string, params?: object) => void;
}

interface AnalyticsProviderProps {
  children?: ReactNode;
  segmentWriteKey?: string;
  heapAnalyticsAppId?: string;
}

const AnalyticsContext = createContext<AnalyticsContextValue | undefined>(
  undefined,
);

const AnalyticsProvider = (props: AnalyticsProviderProps) => {
  const { children, segmentWriteKey, heapAnalyticsAppId } = props;

  const plugins = useMemo<AnalyticsPlugin[]>(() => {
    const result = [];
    if (segmentWriteKey) {
      result.push(new SegmentPlugin(segmentWriteKey));
    }

    if (heapAnalyticsAppId) {
      result.push(new HeapPlugin(heapAnalyticsAppId));
    }

    return result;
  }, [segmentWriteKey, heapAnalyticsAppId]);

  const trackEvent = useCallback(
    (name: string, params?: object) => {
      plugins.forEach((plugin) => {
        if (!isProd()) {
          console.debug(`[track_event] ${name}`, params);
        }
        plugin.trackEvent(name, { ...params, product: "core" });
      });
    },
    [plugins],
  );

  const trackUser = useCallback(
    (name: string, params?: object) => {
      plugins.forEach((plugin) => {
        if (!isProd()) {
          console.debug(`[track_user] ${name}`, params);
        }
        plugin.trackUser(name, { ...params, product: "core" });
      });
    },
    [plugins],
  );

  useEffect(() => {
    plugins.forEach((plugin) => {
      return plugin.addScript();
    });
  }, [plugins]);

  const contextValue = useMemo(() => {
    return { trackEvent, trackUser };
  }, [trackEvent, trackUser]);

  return (
    <AnalyticsContext.Provider value={contextValue}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalytics = () => {
  const contextValue = useContext(AnalyticsContext);
  if (!contextValue) {
    throw new Error(
      "useAnalytics needs to be used inside a AnalyticsProvider.",
    );
  }
  return contextValue;
};

export default AnalyticsProvider;
